import { Box, Icon, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Word } from 'src/feature/localization/component/atom/Word'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { iconMapping } from 'src/util/icon-mapping'
import { calculateRatingFillWidth, getCardRatingColor, getProgressFillStyle } from '../../util/skillGapUtils'

export type CardSkillGapRatingProps = {
  value: number // 0 to 5 scale
  expectedValue: number // 0 to 5 scale
  leading?: string
  isLoading?: boolean
  maxWidth?: number | string
}

export const CardSkillGapRating = observer(({ value, expectedValue, leading, isLoading, maxWidth = '100%' }: CardSkillGapRatingProps) => {
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam

  const fillWidth = calculateRatingFillWidth(value)
  const expectedFillWidth = calculateRatingFillWidth(expectedValue)

  const valueColor = getCardRatingColor(value)
  const expectedValueColor = getCardRatingColor(expectedValue)
  const valueText = value.toFixed(1)
  const expectedValueText = expectedValue.toFixed(1)
  const title = pageTranslation.metricsHeader.skillGapInsights()
  const leadingText = leading || '-'
  const expectedProgressFillStyle = getProgressFillStyle(expectedFillWidth, expectedValueColor, 1, 0.2)
  const currentProgressFillStyle = getProgressFillStyle(fillWidth, valueColor, 2)

  return (
    <Box sx={{ ...styles.root, maxWidth: { xs: '100%', md: maxWidth } }}>
      <AppBox alignItems='center' justifyContent='space-between' width='100%' marginBottom={5}>
        <Typography sx={{ ...styles.title, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${title}: ${leadingText}`}>{title}: <Word>{leadingText}</Word></Typography>
        {!isLoading && (
          <AppBox gap={5} alignItems='center'>
            <Tooltip
              title={pageTranslation.skillGapInsightsDesc()}
              placement='top' arrow>
              <Icon sx={{ height: 15, width: 15, color: 'secondary.main' }} component={iconMapping['InfoIcon']} />
            </Tooltip>
            <Typography sx={styles.title}>{pageTranslation.metricsHeader.expected()}: </Typography>
            <Typography sx={styles.headerRatingText}>{valueText}{' / '}{expectedValueText}</Typography>
          </AppBox>
        )}
      </AppBox>
      {!isLoading && (
        <Box sx={styles.barWrapper}>
          {/* Expected progress fill (background layer) */}
          <Box sx={expectedProgressFillStyle} />

          {/* Current progress fill (top layer) */}
          <Box sx={currentProgressFillStyle} />
        </Box>
      )}
      {isLoading && (
        <AppSkeleton
          count={1}
          direction="column"
          rects={[
            { width: '100%', height: 50, color: 'background.dark' },
          ]}
        />
      )}
    </Box>
  )
})

const styles = {
  root: {
    width: '100%',
    borderRadius: '10px',
    gap: 5,
  },
  barWrapper: {
    position: 'relative',
    width: '100%',
    height: 10,
    minWidth: { xs: '100%', md: 300 },
    backgroundColor: 'background.dark',
    borderRadius: '10px',
  },
  headerRatingText: {
    whiteSpace: 'nowrap',
  },
  title: {
    color: 'text.secondary',
    fontSize: 12,
  },
}
